import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { Modal } from "react-bootstrap"

import CatchCard from "~/library/CatchCard"
import FormikWrapper from "~/library/FormikWrapper"
import FormikInput from "~/library/FormikInput"
import FormikSelect from "~/library/FormikSelect"
import ContactForm from "../Contacts/ContactForm"

import * as Yup from "yup"
import type Account from "~/types/Account"
import getAppConfig from "~/utils/getAppConfig"
import { apiWorkspaceContacts, apiWorkspaceContactCategories } from "~/routes"
import { useToast } from "~/utils/Toast"

const accountSchema = Yup.object({
    name: Yup.string().required("Account name is required"),
    accountTypeId: Yup.string().required("Account type is required"),
    status: Yup.string().required("Status is required"),
    address: Yup.string(),
    website: Yup.string().url("Must be a valid URL. Example: https://www.google.com "),
    industry: Yup.string(),
    historicalSpend: Yup.number().min(0, "Must be positive").nullable(),
    lifetimeValue: Yup.number().min(0, "Must be positive").nullable(),
})

interface AccountFormProps {
    onSubmit: (values: any) => void
    initialValues?: any
    isEditing?: boolean
    accountTypes: Array<{ id: string; name: string }>
    initialContacts?: Array<{ id: string; firstName: string; lastName: string }> | null
}

const AccountForm: React.FC<AccountFormProps> = ({
                                                     onSubmit,
                                                     initialValues,
                                                     isEditing = false,
                                                     accountTypes,
                                                     initialContacts = null,
                                                 }) => {
    const { venueSlug } = getAppConfig()
    const toast = useToast()

    const [contactSearch, setContactSearch] = useState("")
    const [contactResults, setContactResults] = useState<Array<{ id: string; firstName: string; lastName: string }>>([])
    const [selectedContacts, setSelectedContacts] = useState<Array<{ id: string; firstName: string; lastName: string }>>(initialContacts || [])
    const [isSearchingContact, setIsSearchingContact] = useState(false)
    const [showContactDropdown, setShowContactDropdown] = useState(false)

    const [showCreateContactModal, setShowCreateContactModal] = useState(false)
    const [contactCategories, setContactCategories] = useState<Array<{ id: string; name: string }>>([])
    const [isLoadingCategories, setIsLoadingCategories] = useState(false)
    const [isCreatingContact, setIsCreatingContact] = useState(false)

    const defaultValues = {
        name: "",
        accountTypeId: accountTypes.length > 0 ? accountTypes[0].id : "",
        status: "prospect",
        address: "",
        website: "",
        industry: "",
        historicalSpend: null,
        lifetimeValue: null,
    }

    useEffect(() => {
        if (showCreateContactModal && contactCategories.length === 0) {
            fetchContactCategories()
        }
    }, [showCreateContactModal])

    const fetchContactCategories = async () => {
        setIsLoadingCategories(true)
        try {
            const url = apiWorkspaceContactCategories.index.path({ venue_slug: venueSlug })
            const response = await fetch(url)
            const data = await response.json()
            setContactCategories(data.contact_categories || [])
        } catch (error) {
            console.error("Error fetching contact categories:", error)
            toast("Error loading contact categories", "danger")
        } finally {
            setIsLoadingCategories(false)
        }
    }

    useEffect(() => {
        if (contactSearch.length >= 2) {
            setIsSearchingContact(true)
            const timeoutId = setTimeout(async () => {
                try {
                    const url = apiWorkspaceContacts.index.path({ venue_slug: venueSlug })
                    let searchUrl = `${url}?q[first_name_or_last_name_cont]=${encodeURIComponent(contactSearch)}&q[account_id_null]=true`

                    const response = await fetch(searchUrl)
                    const data = await response.json()

                    let filteredContacts = data.contacts || []

                    if (isEditing && initialContacts && initialContacts.length > 0) {
                        const selectedContactsInResults = initialContacts.filter(ic => {
                            const searchLower = contactSearch.toLowerCase()
                            return ic.firstName?.toLowerCase().includes(searchLower) ||
                                ic.lastName?.toLowerCase().includes(searchLower)
                        })

                        filteredContacts = [
                            ...filteredContacts,
                            ...selectedContactsInResults.filter(sc => !filteredContacts.find(fc => fc.id === sc.id))
                        ]
                    }

                    setContactResults(filteredContacts)
                    setShowContactDropdown(true)
                } catch (error) {
                    console.error("Error searching contacts:", error)
                } finally {
                    setIsSearchingContact(false)
                }
            }, 300)

            return () => clearTimeout(timeoutId)
        } else {
            setContactResults([])
            setShowContactDropdown(false)
        }
    }, [contactSearch, venueSlug, isEditing, initialContacts])

    const handleFormSubmit = (values: any) => {
        const formData = {
            name: values.name,
            account_type_id: values.accountTypeId,
            status: values.status,
            address: values.address,
            website: values.website,
            industry: values.industry,
            historical_spend: values.historicalSpend,
            lifetime_value: values.lifetimeValue,
            contact_ids: selectedContacts.map(c => c.id),
        }
        onSubmit(formData)
    }

    const handleCreateContact = async (contactFormValues: any) => {
        setIsCreatingContact(true)
        try {
            const url = apiWorkspaceContacts.create.path({ venue_slug: venueSlug })
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({
                    contact: contactFormValues,
                }),
            })

            const result = await response.json()

            if (result.status === 'ok') {

                const contactUrl = apiWorkspaceContacts.show.path({
                    venue_slug: venueSlug,
                    id: result.prefixId
                })
                const contactResponse = await fetch(contactUrl)
                const contactData = await contactResponse.json()

                const newContact = {
                    id: contactData.contact.id,
                    firstName: contactData.contact.firstName,
                    lastName: contactData.contact.lastName,
                }
                setSelectedContacts([...selectedContacts, newContact])
                setShowCreateContactModal(false)
                toast("Contact created and added successfully", "success")
            } else {
                toast(
                    `Error creating contact. ${result.errors?.join(", ") || "Please try again"}`,
                    "danger"
                )
            }
        } catch (error) {
            console.error("Error creating contact:", error)
            toast("Error creating contact. Please try again", "danger")
        } finally {
            setIsCreatingContact(false)
        }
    }

    const statusOptions = [
        { value: "prospect", label: "Prospect" },
        { value: "active", label: "Active" },
        { value: "past", label: "Past" },
    ]

    const accountTypeOptions = accountTypes.map((type) => ({
        value: type.id,
        label: type.name,
    }))

    const handleRemoveContact = (contactId: string) => {
        setSelectedContacts(selectedContacts.filter(c => c.id !== contactId))
    }

    return (
        <>
            <FormikWrapper
                onSubmit={handleFormSubmit}
                validationSchema={accountSchema}
                initialValues={isEditing ? initialValues : defaultValues}
            >
                <CatchCard title="Account Information">
                    <FormikInput
                        name="name"
                        label="Account Name"
                        placeholder="Company Name"
                        required
                        helpText=""
                    />

                    <FormikSelect
                        name="accountTypeId"
                        label="Account Type"
                        options={accountTypeOptions}
                        required
                        helpText=""
                        noEmptyOption={true}
                    />

                    <FormikSelect
                        name="status"
                        label="Status"
                        options={statusOptions}
                        required
                        helpText=""
                        noEmptyOption={true}
                    />

                    <FormikInput
                        name="industry"
                        label="Industry"
                        placeholder="Technology, Healthcare, etc."
                        helpText=""
                    />

                    <FormikInput
                        name="address"
                        label="Address"
                        placeholder="123 Main St, City, State ZIP"
                        helpText=""
                    />

                    <FormikInput
                        name="website"
                        label="Website"
                        placeholder="https://example.com"
                        helpText=""
                    />

                    <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label mb-0">
                                Contacts
                            </label>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => setShowCreateContactModal(true)}
                            >
                                + Create New Contact
                            </button>
                        </div>
                        <div className="position-relative">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search for contacts to add..."
                                value={contactSearch}
                                onChange={(e) => setContactSearch(e.target.value)}
                                onFocus={() => {
                                    if (contactResults.length > 0) {
                                        setShowContactDropdown(true)
                                    }
                                }}
                            />
                            {isSearchingContact && (
                                <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                                    <span className="spinner-border spinner-border-sm" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </span>
                                </div>
                            )}
                            {showContactDropdown && contactResults.length > 0 && (
                                <div className="dropdown-menu show w-100" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    {contactResults.map((contact) => {
                                        const isAlreadySelected = selectedContacts.find(c => c.id === contact.id)

                                        return (
                                            <button
                                                key={contact.id}
                                                type="button"
                                                className="dropdown-item"
                                                onClick={() => {
                                                    if (!isAlreadySelected) {
                                                        setSelectedContacts([...selectedContacts, contact])
                                                    }
                                                    setContactSearch("")
                                                    setShowContactDropdown(false)
                                                }}
                                                disabled={isAlreadySelected}
                                            >
                                                {contact.firstName} {contact.lastName}
                                                {isAlreadySelected && <span className="text-muted ms-2">(already added)</span>}
                                            </button>
                                        )
                                    })}
                                </div>
                            )}
                            {selectedContacts.length > 0 && (
                                <div className="mt-2">
                                    {selectedContacts.map((contact) => (
                                        <span key={contact.id} className="badge bg-success me-2 mb-2">
                                            {contact.firstName} {contact.lastName}
                                            <button
                                                type="button"
                                                className="btn-close btn-close-white ms-2"
                                                style={{ fontSize: '0.6rem' }}
                                                onClick={() => handleRemoveContact(contact.id)}
                                                aria-label="Remove"
                                            />
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="form-text">
                            {isEditing
                                ? "Add or remove contacts for this account"
                                : "Search existing contacts or create a new one"}
                        </div>
                    </div>
                </CatchCard>

                <CatchCard title="Financial Information">
                    <div className="row">
                        <div className="col-6">
                            <FormikInput
                                name="historicalSpend"
                                label="Historical Spend"
                                type="number"
                                placeholder="0.00"
                                helpText="Total historical spend amount"
                            />
                        </div>
                        <div className="col-6">
                            <FormikInput
                                name="lifetimeValue"
                                label="Lifetime Value"
                                type="number"
                                placeholder="0.00"
                                helpText="Estimated lifetime value"
                            />
                        </div>
                    </div>
                </CatchCard>

                <div className="d-flex">
                    <div className="ms-auto">
                        <Link className="btn btn-secondary me-2" to="/accounts">
                            Cancel
                        </Link>
                        <button type="submit" className="btn btn-primary">
                            {isEditing ? "Update Account" : "Create Account"}
                        </button>
                    </div>
                </div>
            </FormikWrapper>

            <Modal show={showCreateContactModal} onHide={() => !isCreatingContact && setShowCreateContactModal(false)} size="lg">
                <Modal.Header closeButton={!isCreatingContact}>
                    <Modal.Title>Create New Contact</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isLoadingCategories ? (
                        <div className="text-center py-4">
                <span className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </span>
                        </div>
                    ) : contactCategories.length > 0 ? (
                        <ContactForm
                            onSubmit={handleCreateContact}
                            contactCategories={contactCategories}
                            isEditing={false}
                            isModal={true}
                            isSubmitting={isCreatingContact}
                        />
                    ) : (
                        <div className="alert alert-warning">
                            No contact categories found. Please create contact categories first.
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </>
    )
}

export default AccountForm